import {Button} from '@primer/react'
import {Link} from '@github-ui/react-core/link'
import {DockedPanel, useDockedPanelActions, type PanelMetadata} from '../../../contexts/DockedPanelContext'
import {useRef} from 'react'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'

type AnalyticsPayload = Record<string, unknown>
type SendAnalytics = (payload: AnalyticsPayload) => void

export type ActionConfig =
  | {linkTo: string; label: string; ariaLabel?: string; analyticsPayload?: AnalyticsPayload}
  | {onClick: () => void; label: string; ariaLabel?: string; analyticsPayload?: AnalyticsPayload}
  | {
      panel: DockedPanel
      label: string
      metadata?: PanelMetadata
      ariaLabel?: string
      analyticsPayload?: AnalyticsPayload
    }

export function Action({action}: {action: ActionConfig}) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const sendAnalytics: SendAnalytics = payload => {
    sendPullRequestAnalyticsEvent('overview.action_item_click', 'OVERVIEW_ACTION_ITEM', payload)
  }

  if ('linkTo' in action) {
    return <LinkAction action={action} sendAnalytics={sendAnalytics} />
  }

  if ('panel' in action) {
    return <DockedPanelAction action={action} sendAnalytics={sendAnalytics} />
  }

  return (
    <Button
      type="button"
      variant="invisible"
      size="small"
      aria-label={action.ariaLabel}
      onClick={() => {
        sendAnalytics({action_type: 'click', label: action.label, ...action.analyticsPayload})
        action.onClick()
      }}
    >
      {action.label}
    </Button>
  )
}

// Link actions navigate to a different route. Close any open docked panel
function LinkAction({
  action,
  sendAnalytics,
}: {
  action: Extract<ActionConfig, {linkTo: string}>
  sendAnalytics: SendAnalytics
}) {
  const {closePanel} = useDockedPanelActions()
  return (
    <Button
      as={Link}
      to={action.linkTo}
      variant="invisible"
      size="small"
      aria-label={action.ariaLabel}
      onClick={() => {
        sendAnalytics({action_type: 'link', label: action.label, ...action.analyticsPayload})
        closePanel()
      }}
    >
      {action.label}
    </Button>
  )
}

// Separate component for panel actions to manage focus return after closing the panel
function DockedPanelAction({
  action,
  sendAnalytics,
}: {
  action: Extract<ActionConfig, {panel: string}>
  sendAnalytics: SendAnalytics
}) {
  const {openPanel} = useDockedPanelActions()
  const ref = useRef<HTMLButtonElement>(null)

  return (
    <Button
      type="button"
      variant="invisible"
      size="small"
      onClick={() => {
        sendAnalytics({action_type: 'panel', label: action.label, panel: action.panel, ...action.analyticsPayload})
        openPanel(action.panel, {metadata: action.metadata, returnFocusTo: ref.current ?? undefined})
      }}
      ref={ref}
      aria-label={action.ariaLabel}
    >
      {action.label}
    </Button>
  )
}
